import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { AppColors } from "../../constants/AppColors";
import { StatisticsOverviewResponse } from "../../models/ResponseModels/StatisticsSummaryResponse";
import { formatMoney } from "./statisticsHelpers";

interface AnalyticsHeroCardProps {
  overview: StatisticsOverviewResponse;
  t: (key: string, values?: Record<string, string | number>) => string;
}

const DonutChart = ({
  percentage,
  value,
  label,
  positive,
}: {
  percentage: number;
  value: string;
  label: string;
  positive: boolean;
}) => {
  const safePercentage = Math.max(0, Math.min(100, Number(percentage || 0)));
  const size = 132;
  const strokeWidth = 15;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (safePercentage / 100) * circumference;

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          stroke="rgba(255,255,255,0.25)"
          fill="none"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />

        <circle
          stroke={positive ? AppColors.textOnPrimary : AppColors.danger}
          fill="none"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontWeight: 950, color: AppColors.textOnPrimary }}>
          {value}
        </Typography>

        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 800,
            color: "rgba(255,255,255,0.82)",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

const AnalyticsHeroCard = ({ overview, t }: AnalyticsHeroCardProps) => {
  const profitPositive = overview.net_profit >= 0;

  return (
    <Card
      className="rp-card-no-accent"
      sx={{
        mb: 2,
        borderRadius: 4,
        color: AppColors.textOnPrimary,
        background: `linear-gradient(135deg, ${AppColors.primary} 0%, #1d4ed8 45%, #0f172a 100%)`,
        overflow: "hidden",
        position: "relative",
        border: "none",
        boxShadow: AppColors.shadowPanel,
        "& > .MuiCardContent-root": {
          position: "relative",
          zIndex: 2,
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          sx={{
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            position: "relative",
          }}
        >
          <Box sx={{ maxWidth: 680 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 850,
                color: "rgba(255,255,255,0.75)",
                textTransform: "uppercase",
                letterSpacing: 0.8,
                mb: 0.5,
              }}
            >
              {t("statistics.netProfit")}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 34, md: 46 },
                lineHeight: 1.05,
                fontWeight: 950,
              }}
            >
              {formatMoney(overview.net_profit, overview.currency)}
            </Typography>

            <Typography
              sx={{
                mt: 1,
                color: "rgba(255,255,255,0.78)",
                fontWeight: 650,
              }}
            >
              {t("statistics.revenueExpensesHint", {
                revenue: formatMoney(overview.revenue, overview.currency),
                expenses: formatMoney(
                  overview.total_expenses,
                  overview.currency,
                ),
              })}
            </Typography>
          </Box>

          <DonutChart
            percentage={overview.profit_margin_percentage}
            value={`${Math.round(overview.profit_margin_percentage || 0)}%`}
            label={t("statistics.margin")}
            positive={profitPositive}
          />
        </Stack>

        <Box
          sx={{
            mt: 3,
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 1.25,
            position: "relative",
          }}
        >
          {[
            [t("statistics.bookings"), overview.booking_count],
            [t("statistics.nights"), overview.booked_nights],
            [
              t("statistics.occupancy"),
              `${Number(overview.occupancy_percentage || 0).toFixed(1)}%`,
            ],
            [
              t("statistics.averageBooking"),
              formatMoney(overview.average_booking_value, overview.currency),
            ],
          ].map(([label, value]) => (
            <Box
              key={String(label)}
              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.16)",
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.74)",
                  fontSize: 12,
                  fontWeight: 750,
                }}
              >
                {label}
              </Typography>

              <Typography sx={{ mt: 0.5, fontWeight: 950, fontSize: 19 }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AnalyticsHeroCard;
